<template>
  <SoAdminPage
    :title="
      selectedTemplate
        ? `Subject: ${selectedTemplate.subject}`
        : 'Email Template'
    "
    :back-to="{ name: RouteNames.AdminEmailTemplates }"
  >
    <template #subtitle>
      <div v-if="communication" class="d-flex align-center ga-2">
        <SoChip :type="SoChipType.IMPORTANT" :text="EmailTemplateSender.NOVA" />
        <span>Channel: {{ communication.name }}</span>
      </div>
    </template>

    <template #headerRight>
      <div class="d-flex flex-nowrap align-center ga-2">
        <EmailTemplateVersionSelect
          v-if="versions.length"
          v-model="selectedVersionId"
          class="version-select"
          :versions="versions"
        />
        <SoButton
          :disabled="!selectedTemplate || selectedTemplate.isLive"
          :loading="!!selectedTemplate && pendingId === selectedTemplate.id"
          :title="publishLabel"
          type="secondary"
          large
          class="publish-button"
          data-testid="publish-version"
          @click="openPublishConfirm"
        />
        <SoButton
          :disabled="!selectedTemplate"
          title="New"
          type="primary"
          icon="plus"
          large
          data-testid="edit-version"
          @click="cloneVersion"
        />
      </div>
    </template>

    <div
      v-if="query.isFetching && !versions.length"
      class="d-flex justify-center pa-8"
    >
      <SoProgress indeterminate />
    </div>
    <SoAlert
      v-else-if="!versions.length"
      type="info"
      text="No templates found for this communication."
      data-testid="no-versions-alert"
    />
    <div v-else-if="selectedTemplate" class="detail-content">
      <SoDivider class="mb-4" />
      <EmailTemplatePreview
        fill-height
        class="template-preview"
        :html-content="selectedTemplate.htmlContent"
        :plain-content="selectedTemplate.plainContent"
        :render-debounce-ms="0"
      >
        <template #headerLeft>
          <EmailTemplateValidationSummary
            :findings="validationFindings"
            :assets="templateAssets"
            :variables="templateVariables"
            :links="templateLinks"
            :checking-assets="checkingAssets"
          />
        </template>
        <template #toggleStart>
          <SoButton
            type="text"
            title="Template info"
            data-testid="version-details-button"
            @click="showDetails = true"
          />
          <SoButton
            v-if="mandrillUrl"
            type="text"
            title="Mandrill activity"
            icon="external-link sm"
            :href="mandrillUrl"
            target="_blank"
            data-testid="mandrill-activity"
          />
        </template>
      </EmailTemplatePreview>

      <SoDialog
        v-model="showDetails"
        card-title="Version details"
        :max-width="480"
        data-testid="version-details-dialog"
      >
        <div class="d-flex flex-column ga-4">
          <div>
            <div class="text-caption text-medium-emphasis">Published at</div>
            <div>
              {{
                selectedTemplate.publishedAt
                  ? formatDateTime(selectedTemplate.publishedAt)
                  : "—"
              }}
            </div>
          </div>
          <div v-if="selectedTemplate.createdAt">
            <div class="text-caption text-medium-emphasis">Created at</div>
            <div>{{ formatDateTime(selectedTemplate.createdAt) }}</div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">Modified by</div>
            <div>{{ selectedTemplate.modifiedBy ?? "—" }}</div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">
              Reason for change
            </div>
            <div>{{ selectedTemplate.modifiedReason || "—" }}</div>
          </div>
        </div>
      </SoDialog>

      <EmailTemplatePublishConfirmDialog
        v-model="showPublishConfirm"
        :html-content="selectedTemplate.htmlContent"
        :plain-content="selectedTemplate.plainContent"
        :publishing="pendingId === selectedTemplate.id"
        @confirm="confirmPublish"
      />
      <EmailTemplateSyncDialog v-model="showSync" :payload="syncPayload" />
    </div>
  </SoAdminPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { RouteNames } from "@/types/router"
  import { EmailTemplatesService } from "@/services/admin/EmailTemplatesService"
  import { EmailTemplatesMutator } from "@/mutators/emailTemplates/EmailTemplatesMutator"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { formatDateTime } from "@/helpers/dateHelper"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import SoDivider from "@/components/SoDivider.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import EmailTemplateVersionSelect from "./components/EmailTemplateVersionSelect.vue"
  import EmailTemplatePreview from "./components/EmailTemplatePreview.vue"
  import EmailTemplateValidationSummary from "./components/EmailTemplateValidationSummary.vue"
  import EmailTemplatePublishConfirmDialog from "./components/EmailTemplatePublishConfirmDialog.vue"
  import EmailTemplateSyncDialog from "./components/EmailTemplateSyncDialog.vue"
  import { EmailTemplateValidationService } from "@/services/admin/emailTemplateValidation/EmailTemplateValidationService"
  import {
    AssetCheck,
    ValidationFinding,
  } from "@/services/admin/emailTemplateValidation/emailTemplateValidation.types"
  import {
    EmailTemplateSender,
    ICommunication,
    IEmailTemplate,
    IEmailTemplateSyncPayload,
  } from "@/models/admin/emailTemplates/emailTemplates.types"
  import { mandrillActivityUrl } from "./helpers/mandrillLink"
  import { toSyncPayload } from "./helpers/emailTemplateSync"

  export default defineComponent({
    components: {
      SoAdminPage,
      SoButton,
      SoChip,
      SoDivider,
      SoDialog,
      SoAlert,
      SoProgress,
      EmailTemplateVersionSelect,
      EmailTemplatePreview,
      EmailTemplateValidationSummary,
      EmailTemplatePublishConfirmDialog,
      EmailTemplateSyncDialog,
    },
    data() {
      return {
        RouteNames,
        SoChipType,
        EmailTemplateSender,
        snackbar: useSnackbarStore(),
        query: EmailTemplatesService.getEmailTemplatesByCommunication(
          Number(this.$route.params.communicationId),
        ),
        setLiveMutator: EmailTemplatesMutator.setLiveVersion(),
        selectedVersionId: null as number | null,
        targetVersion: this.$route.query.version
          ? Number(this.$route.query.version)
          : null,
        pendingId: null as number | null,
        templateAssets: [] as AssetCheck[],
        checkingAssets: false,
        showDetails: false,
        showPublishConfirm: false,
        showSync: false,
        syncPayload: null as IEmailTemplateSyncPayload | null,
      }
    },
    computed: {
      communicationId(): number {
        return Number(this.$route.params.communicationId)
      },
      versions(): IEmailTemplate[] {
        return [...(this.query.data ?? [])].sort(
          (a, b) => b.version - a.version,
        )
      },
      communication(): ICommunication | null {
        return this.versions[0]?.communication ?? null
      },
      selectedTemplate(): IEmailTemplate | null {
        if (!this.versions.length) return null
        return (
          this.versions.find(
            (template) => template.id === this.selectedVersionId,
          ) ?? this.versions[0]
        )
      },
      // Publishing an older version rolls the live version back to it.
      publishLabel(): string {
        if (!this.selectedTemplate) return "Publish This Version"
        const isLatest = this.selectedTemplate.id === this.versions[0]?.id
        // Only an older, not-yet-live version publishes as a rollback.
        if (!isLatest && !this.selectedTemplate.isLive) {
          return "Restore this version"
        }
        return "Publish This Version"
      },
      mandrillUrl(): string | null {
        return mandrillActivityUrl(this.selectedTemplate?.subject ?? null)
      },
      templateVariables(): string[] {
        if (!this.selectedTemplate) return []
        return EmailTemplateValidationService.extractVariables(
          this.selectedTemplate.htmlContent,
          this.selectedTemplate.plainContent,
        )
      },
      templateLinks(): string[] {
        if (!this.selectedTemplate) return []
        return EmailTemplateValidationService.extractLinks(
          this.selectedTemplate.htmlContent,
          this.selectedTemplate.plainContent,
        )
      },
      validationFindings(): ValidationFinding[] {
        if (!this.selectedTemplate) return []
        return [
          ...EmailTemplateValidationService.runStaticChecks({
            htmlContent: this.selectedTemplate.htmlContent,
            plainContent: this.selectedTemplate.plainContent,
          }),
          ...EmailTemplateValidationService.assetFindings(this.templateAssets),
        ]
      },
    },
    watch: {
      versions: {
        immediate: true,
        handler(versions: IEmailTemplate[]) {
          if (!versions.length) return
          if (this.targetVersion !== null) {
            const created = versions.find(
              (template) => template.version === this.targetVersion,
            )
            if (!created) return
            this.selectedVersionId = created.id
            this.targetVersion = null
            return
          }
          if (this.selectedVersionId === null) {
            this.selectedVersionId = versions[0].id
          }
        },
      },
      communicationId(communicationId: number) {
        this.selectedVersionId = null
        this.targetVersion = null
        this.query =
          EmailTemplatesService.getEmailTemplatesByCommunication(
            communicationId,
          )
      },
      // Re-probe assets for the selected version. Immediate so cached versions
      // (already present at mount, e.g. on a return visit) are checked too.
      selectedVersionId: {
        immediate: true,
        handler() {
          this.templateAssets = []
          this.checkAssets()
        },
      },
    },
    methods: {
      formatDateTime,
      // Probing waits on the network per asset, so a version switched away from
      // mid-probe must not land its results on the one now on screen.
      async checkAssets() {
        const template = this.selectedTemplate
        if (!template) return
        const requested = `${this.communicationId}-${template.id}`
        const current = () =>
          `${this.communicationId}-${this.selectedTemplate?.id}`
        this.checkingAssets = true
        try {
          const assets = await EmailTemplateValidationService.checkAssets({
            htmlContent: template.htmlContent,
            plainContent: template.plainContent,
          })
          if (current() !== requested) return
          this.templateAssets = assets
        } finally {
          if (current() === requested) this.checkingAssets = false
        }
      },
      cloneVersion() {
        if (!this.selectedTemplate) return
        this.$router.push({
          name: RouteNames.AdminEmailTemplatesCreate,
          query: {
            communicationId: String(this.communicationId),
            cloneFromId: String(this.selectedTemplate.id),
          },
        })
      },
      openPublishConfirm() {
        if (this.selectedTemplate && !this.selectedTemplate.isLive) {
          this.showPublishConfirm = true
        }
      },
      async confirmPublish() {
        if (this.selectedTemplate) await this.makeLive(this.selectedTemplate)
      },
      async makeLive(template: IEmailTemplate) {
        this.pendingId = template.id
        try {
          await this.setLiveMutator.mutateAsync({
            id: template.id,
            communicationId: this.communicationId,
          })
          this.snackbar.addSuccess(`Version ${template.version} is now live`)
          await this.query.refetch()
          this.showPublishConfirm = false
          this.syncPayload = toSyncPayload(
            { ...template, communicationName: template.communication.name },
            true,
          )
          this.showSync = true
        } catch {
          this.snackbar.addError("Failed to update live version")
        } finally {
          this.pendingId = null
        }
      },
    },
  })
</script>

<style scoped>
  .detail-content {
    display: flex;
    flex-direction: column;
    /* Fill the viewport below the nav + page header without overflowing it. */
    height: calc(100vh - 270px);
  }

  .template-preview {
    flex: 1;
    min-height: 0;
  }

  .version-select :deep(.v-input) {
    margin-bottom: 0 !important;
  }

  /* Fixed width keeps the header stable as the label swaps between
     "Publish This Version" and "Restore this version". */
  .publish-button :deep(.v-btn) {
    width: 170px;
  }
</style>
